import React, { useCallback, useMemo, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import HelpPanelButton from '@atlassian/jira-help-panel-button/src/index.tsx';
import FileCabinetOpen from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/file-cabinet-open/index.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	useChildIssuesLimit,
	useIsModal,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import { IN_PRODUCT_HELP } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import { getChildIssuesLimitExceededAnalyticsCount } from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';
import { TIME_TRACKING } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useProjectType,
	useIsSimplifiedProject,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { generateViewInSearchLink } from '../../common/generate-view-in-search-link/index.tsx';
import type { ChildIssuesPanelType } from '../../model/types.tsx';
import { useChildIssuesLimitHelp } from '../../services/use-child-issues-limit-help/index.tsx';
import { ButtonLinkWithShortcutIcon } from '../button-link-with-shortcut-icon/index.tsx';
import FileCabinet from './assets/file-cabinet.svg';
import messages from './messages.tsx';

type Props = {
	hasExceededIssuesLimitInitialLoad: boolean;
	childIssuesPanelType: ChildIssuesPanelType;
};

const TEST_ID = 'issue.views.common.child-issues-panel.child-issues-limit-panel';
const LEARN_MORE_LINK_TEST_ID = `${TEST_ID}.learn-more-link`;
export const VIEW_IN_SEARCH_BUTTON_TEST_ID = `${TEST_ID}.view-in-search-button`;

export const ChildIssuesLimitPanel = ({
	hasExceededIssuesLimitInitialLoad,
	childIssuesPanelType,
}: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const isModal = useIsModal();
	const [{ value: fieldConfig }] = useFieldConfig(issueKey, TIME_TRACKING);
	const { push } = useRouterActions();

	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const isSimplifiedProject = useIsSimplifiedProject(projectKey);

	const hasTimetracking = Boolean(fieldConfig);

	const childIssuesLimit = useChildIssuesLimit();

	const onInProductHelpLinkClicked = useCallback(
		(event: MouseEvent, analyticsEvent: AnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'learnMoreAboutChildIssues', {
				issueCount: getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit),
			});
		},
		[childIssuesLimit],
	);

	const childIssuesLimitHelp = useChildIssuesLimitHelp();

	const viewInSearchLink = useMemo(() => {
		// These values will never be missing when the FF is enabled, this check is to satisfy flow
		if (
			projectKey === undefined ||
			projectType === undefined ||
			isSimplifiedProject === undefined
		) {
			logger.safeErrorWithoutCustomerData(
				'issue.child-issues-panel.child-issues-limit-panel',
				'Missing project key, type, or isSimplified when generating search link',
			);
			return '';
		}

		return generateViewInSearchLink(
			projectKey,
			projectType,
			isSimplifiedProject,
			issueKey,
			childIssuesPanelType,
			hasExceededIssuesLimitInitialLoad,
		);
	}, [
		childIssuesPanelType,
		hasExceededIssuesLimitInitialLoad,
		isSimplifiedProject,
		issueKey,
		projectKey,
		projectType,
	]);

	const onViewInSearchClicked = useCallback(
		(event: MouseEvent, analyticsEvent: AnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'viewInSearch', {
				issueCount: getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit),
			});

			// Ignore cmd or ctrl clicks - allow user to open in new tab
			if (!event.metaKey && !event.ctrlKey) {
				event.preventDefault();
				push(viewInSearchLink);
			}
		},
		[childIssuesLimit, push, viewInSearchLink],
	);

	const fallbackComponent = (
		<ButtonLinkWithShortcutIcon
			href={childIssuesLimitHelp.href}
			onClick={onInProductHelpLinkClicked}
			target="_blank"
			testId={LEARN_MORE_LINK_TEST_ID}
		>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? messages.learnMoreAboutChildIssuesLinkIssueTermRefresh
					: messages.learnMoreAboutChildIssuesLink,
			)}
		</ButtonLinkWithShortcutIcon>
	);

	const withTimetrackingChildIssuesLimitDescriptionMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.withTimetrackingChildIssuesLimitDescriptionIssueTermRefresh
		: messages.withTimetrackingChildIssuesLimitDescription;

	const withoutTimetrackingChildIssuesLimitDescriptionMessage = fg(
		'jira-issue-terminology-refresh-m3',
	)
		? messages.withoutTimetrackingChildIssuesLimitDescriptionIssueTermRefresh
		: messages.withoutTimetrackingChildIssuesLimitDescription;

	return (
		<ChildrenLimitPanelContainer data-testid={TEST_ID}>
			<ImgContainer>
				{fg('move_issue_view_assets_into_central_location') && (
					<Box xcss={imageWrapperStyles}>
						<FileCabinetOpen alt="" />
					</Box>
				)}
				{!fg('move_issue_view_assets_into_central_location') && (
					<FileCabinetImg src={FileCabinet} alt="" />
				)}
			</ImgContainer>
			<DescriptionContainer>
				<Heading size="small">
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? messages.childIssuesLimitTitleIssueTermRefresh
							: messages.childIssuesLimitTitle,
					)}
				</Heading>
				<LimitDescription>
					<FormattedMessage
						{...(hasTimetracking
							? withTimetrackingChildIssuesLimitDescriptionMessage
							: withoutTimetrackingChildIssuesLimitDescriptionMessage)}
						values={{
							learnMoreLink:
								childIssuesLimitHelp.type === IN_PRODUCT_HELP && !isModal ? (
									<HelpPanelButton
										appearance="link"
										spacing="none"
										articleId={childIssuesLimitHelp.articleId}
										fallbackComponent={fallbackComponent}
										onClick={onInProductHelpLinkClicked}
										testId={LEARN_MORE_LINK_TEST_ID}
									>
										{formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.learnMoreAboutChildIssuesLinkIssueTermRefresh
												: messages.learnMoreAboutChildIssuesLink,
										)}
									</HelpPanelButton>
								) : (
									fallbackComponent
								),
							limit: childIssuesLimit,
						}}
					/>
				</LimitDescription>
				<Button
					appearance="default"
					href={viewInSearchLink}
					onClick={onViewInSearchClicked}
					testId={VIEW_IN_SEARCH_BUTTON_TEST_ID}
					interactionName="auto-generated.jira-issue-view-common-views.child-issues-panel.view.child-issues-limit-panel.button"
				>
					{formatMessage(messages.viewInSearchButton)}
				</Button>
			</DescriptionContainer>
		</ChildrenLimitPanelContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChildrenLimitPanelContainer = styled.div({
	boxShadow: token('elevation.shadow.raised'),
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.raised'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${0.375 * gridSize}px`,
	marginTop: token('space.150'),
	marginBottom: token('space.100'),
});

const imageWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${6.125 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${8 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FileCabinetImg = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${6.125 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${8 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImgContainer = styled.div({
	flex: 1,
	marginLeft: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled.div({
	marginTop: token('space.200'),
	marginRight: token('space.200'),
	marginBottom: token('space.200'),
	marginLeft: token('space.200'),
	wordBreak: 'break-word',
	flex: 9,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LimitDescription = styled.p({
	fontFamily: token('font.family.body'),
	marginTop: token('space.100'),
	marginRight: '0px',
	marginBottom: token('space.100'),
	marginLeft: '0px',
});
